import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { AuthService } from "../../../auth/auth.service";
import { MessagingService } from "../../../core/messaging/messaging.service";
import { SeverityType } from "../../../core/messaging/severity-type.enum";
import { FormService } from "../../../dynamic-forms/form.service";
import { Dropdown } from "../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { DynamicInput } from "../../../dynamic-forms/inputs/dynamic-input.model";
import { SelectableInput } from "../../../dynamic-forms/inputs/selectable-input.model";
import { Textarea } from "../../../dynamic-forms/inputs/textarea/textarea.model";
import { Textbox } from "../../../dynamic-forms/inputs/textbox/textbox.model";
import { CustomPendCode } from "../../../platform/modules/service-org-admin/service-org-config/model/custom-pend-code.model";
import { ServiceOrgConfigurationService } from "../../../platform/modules/service-org-admin/service-org-config/service-org-config.service";
import { NumberHelper } from "../../../utilities/contracts/number-helper";
import { StringHelper } from "../../../utilities/contracts/string-helper";
import { RegExHelper } from "../../../utilities/reg-Ex-Helper";
@Component({
  selector: "app-create-new-pend-modal",
  templateUrl: "./create-new-pend-modal.component.html",
  styleUrls: ["./create-new-pend-modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateNewPendModalComponent implements OnInit {
  inputs: DynamicInput[];
  isConfirmationModalVisible = false;
  customPend: CustomPendCode;
  pendCodeId: number;
  isEditMode = false;
  saveOrCreate = "";
  headerText = "";
  @Input() isEditPendCode = false;
  private pVisible = false;
  @Input()
  get visible(): boolean {
    return this.pVisible;
  }
  set visible(value: boolean) {
    this.pVisible = value;
    if (NumberHelper.isGreaterThan(this.customPendTypeId, 0) && this.isEditPendCode && value) {
      this.saveOrCreate = "Save";
      this.headerText = "Edit Pend Code";
      this.getPendCodeList(this.customPendTypeId);
    } else {
      this.saveOrCreate = "Create";
      this.headerText = "Create New Pend";
      this.resetForm();
      this.setInputs();
    }
  }
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onShow = new EventEmitter<null>(true);
  @Output() onHide = new EventEmitter<null>(true);
  @Output() pendCodeCategoryListUpdate: EventEmitter<void> = new EventEmitter();
  form: FormGroup;

  pendCodesInput: Textbox;
  descriptionInput: Textarea;
  clinicalFlagInput: Dropdown;
  ownerInput: Dropdown;
  autoCloseChaseInput: Dropdown;

  private customPendTypeId: number;
  @Input()
  get pendCodeTypeId(): number {
    return this.customPendTypeId;
  }
  set pendCodeTypeId(value: number) {
    this.customPendTypeId = value;
  }

  constructor(
    protected readonly formService: FormService,
    protected readonly changeDetector: ChangeDetectorRef,
    protected readonly authService: AuthService,
    protected readonly pendService: ServiceOrgConfigurationService,
    private readonly messagingService: MessagingService

  ) {}

  ngOnInit() {
    this.initializeForm();
    this.checkIsEditMode();
    this.setInputs();
    this.createForm();
  }


  private initializeForm(): void {

    this.pendCodesInput = new Textbox({
    key: "pendName",
    label: "Pend Name",
    placeholder: "Enter Pend Name",
    validators: [Validators.minLength(2), Validators.maxLength(100), Validators.pattern(RegExHelper.alphaNumeric)],
    errorMessages: {
      minlength: "Pend Name must have minimum of 2 characters.",
      maxlength: "Pend Name must have maximum of 100 characters.",
      pattern: "Pend Name must not contain symbols or special characters.",
    },
  });

    this.descriptionInput = new Textarea({
    key: "pendDesc",
    label: "Pend Code Description",
    placeholder: "Enter a Pend Code Description",
    validators: [Validators.minLength(4), Validators.maxLength(250)],
    errorMessages: {
      minlength: "Pend Code Description must have minimum of 4 characters.",
      maxlength: "Pend Code Description must have maximum of 250 characters.",
    },
  });

    this.clinicalFlagInput = new Dropdown({
    key: "flag",
    label: "Flag As Clinical",
    placeholder: "Select from List",
    options: [
      new SelectableInput({ text: "Yes", value: "1" }),
      new SelectableInput({ text: "No", value: "0" }),
    ],
    appendTo: "body",
    tooltip: "Any chases that have already been pended will retain the clinical flag it had at the time it was pended. All pends created moving forward will have the new clinical flag rules applied to it.",
  });

    this.ownerInput = new Dropdown({
    key: "owner",
    label: "Default Owner",
    placeholder: "Select from List",
    options: [
      new SelectableInput({ text: "Reveleer", value: "O" }),
      new SelectableInput({ text: "Customer", value: "C" }),
      ],
    appendTo: "body",
    tooltip: "Any chases that have already been pended will retain the owner it had at the time it was pended. All pends created moving forward will have the new owner rules applied to it.",
  });

    this.autoCloseChaseInput = new Dropdown({
    key: "autoCloseChase",
    label: "Auto Close Chase",
    placeholder: "Select from List",
    options: [
      new SelectableInput({ text: "Yes", value: "1" }),
      new SelectableInput({ text: "No", value: "0" }),
    ],
    appendTo: "body",
    tooltip: "Any chases that have already been pended will retain the auto close status it had at the time it was pended. All pends created moving forward will have the new auto close rules applied to it.",
  });

  }

  setInputs(): void {
    this.inputs = [
      this.pendCodesInput,
      this.descriptionInput,
      this.ownerInput,
      this.autoCloseChaseInput,
      this.clinicalFlagInput,
    ];
    this.formService.updateDom.next();
  }

  createForm(): void {
    this.form = this.formService.createFormGroup([
      this.pendCodesInput,
      this.descriptionInput,
      this.ownerInput,
      this.autoCloseChaseInput,
      this.clinicalFlagInput,
    ]);
  }

  resetForm(): void {
    if (this.form) {
      this.initializeForm();
      this.form.reset();
      this.formService.updateDom.next();
      }
    this.changeDetector.markForCheck();
  }
  save(): void {
      if (this.form.valid && StringHelper.isAvailable(this.form.get(this.pendCodesInput.key).value) && StringHelper.isAvailable(this.form.get(this.descriptionInput.key).value)
          && StringHelper.isAvailable(this.form.get(this.ownerInput.key).value) && StringHelper.isAvailable(this.form.get(this.autoCloseChaseInput.key).value)
          && StringHelper.isAvailable(this.form.get(this.clinicalFlagInput.key).value)) {
      this.customPend = new CustomPendCode({
        pendTypeId: this.saveOrCreate === "Save" ? this.pendCodeId : 0,
        name: this.form.controls.pendName.value,
        description: this.form.controls.pendDesc.value,
        isClinical: this.form.controls.flag.value === "1",
        defaultOwner: this.form.controls.owner.value,
        isAutoClose: this.form.controls.autoCloseChase.value === "1",
        });
      this.pendService.saveCustomPendsCode(this.customPend)
          .subscribe(pendCodeId => {

            if (this.saveOrCreate === "Create") {
            this.messagingService.showToast("New Pend Code created successfully.", SeverityType.SUCCESS);
            } else {
            this.messagingService.showToast("Pend Code updated successfully.", SeverityType.SUCCESS);
            }

            this.pendCodeId = pendCodeId;
            this.changeDetector.markForCheck();
            this.pendCodeCategoryListUpdate.emit();
            this.visible = false;
            this.createForm();
            this.changeDetector.markForCheck();
          });

    } else {
      this.pendCodesInput = new Textbox({
        ...this.pendCodesInput,
        validators: [Validators.required, Validators.minLength(2), Validators.maxLength(100), Validators.pattern(RegExHelper.alphaNumeric)],
        errorMessages: {
          required: "Pend Name is required.",
          minlength: "Pend Name must have minimum of 2 characters.",
          maxlength: "Pend Name must have maximum of 100 characters.",
          pattern: "Pend Name must not contain symbols or special characters.",
        },
      } as any);
      this.descriptionInput = new Textarea({
        ...this.descriptionInput,
        validators: [Validators.required, Validators.minLength(4), Validators.maxLength(250)],
        errorMessages: {
          required: "Pend Code Description is required.",
          minlength: "Pend Code Description must have minimum of 4 characters.",
          maxlength: "Pend Code Description must have maximum of 250 characters.",
        },
      } as any);
      this.clinicalFlagInput = new Dropdown({
        ...this.clinicalFlagInput,
        validators: [Validators.required],
        errorMessages: {
          required: "Flag As Clinical is required.",
        },
      } as any);
      this.ownerInput = new Dropdown({
        ...this.ownerInput,
        validators: [Validators.required],
        errorMessages: {
          required: "Default Owner is required.",
        },
      } as any);
      this.autoCloseChaseInput = new Dropdown({
        ...this.autoCloseChaseInput,
        validators: [Validators.required],
        errorMessages: {
          required: "Auto Close Chase is required.",
        },
      } as any);
      this.setValidators([this.pendCodesInput, this.descriptionInput, this.ownerInput, this.clinicalFlagInput, this.autoCloseChaseInput]);
      this.checkFormGroupInvalid();
      this.changeDetector.markForCheck();
      this.markAllAsTouched();
      this.setInputs();
    }
  }

  private setValidators(controls: DynamicInput[]): void {
    controls.forEach(control => {
      this.form.get(control.key).setValidators(control.validators);
    });
  }

  private checkFormGroupInvalid(): void {
    this.formService.markAllAsTouched(this.form);
    this.changeDetector.markForCheck();
    return;
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onHide.emit();
    this.resetForm();
    this.createForm();
    this.form.reset();
  }

  checkIsEditMode() {
    this.saveOrCreate = this.customPendTypeId ? "Save" : "Create";
    this.headerText = this.customPendTypeId ? "Edit Pend Code" : "Create New Pend";
  }

  getPendCodeList(pendCodeId: any): void {
    this.pendService.getPendCodeByTypeId(pendCodeId)
      .subscribe(data => {
        this.pendCodeId = data.pendTypeId;
        this.ownerInput = new Dropdown({...this.ownerInput, hasTooltipLabel: true });
        this.autoCloseChaseInput = new Dropdown({...this.autoCloseChaseInput, hasTooltipLabel: true });
        this.clinicalFlagInput = new Dropdown({ ...this.clinicalFlagInput, hasTooltipLabel: true });
        this.setInputs();
        this.form.get(this.pendCodesInput.key).setValue(data.name);
        this.form.get(this.descriptionInput.key).setValue(data.description);
        this.form.get(this.ownerInput.key).setValue(data.defaultOwner);
        this.form.get(this.autoCloseChaseInput.key).setValue(data.isAutoClose ? "1" : "0");
        this.form.get(this.clinicalFlagInput.key).setValue(data.isClinical ? "1" : "0");
        this.changeDetector.markForCheck();
      });
  }

  protected markAllAsTouched(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control.markAsTouched({ onlySelf: true });
      control.markAsDirty({ onlySelf: true });
    });
    this.formService.updateDom.next();
  }

  cancelConfirmation(): void {
    this.isConfirmationModalVisible = false;
  }

  trackByIndex(index): number {
    return index;
  }
}
